import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import eatingWaySettingService from 'features/EatingWay/eatingWaySettingService';
import {
  eatingWaySettingActions,
  selectEatingWaySettingData,
  selectEatingWaySettingSelectedItemIds,
} from 'features/EatingWay/eatingWaySettingSlice';
import { EatingWaySettingListItem, EatingWaySettingTimeFrame } from 'features/EatingWay/models';
import { mutliDragAwareReorder } from 'features/EatingWay/utils/dragutils/utils';
import SelectMenuBookModal, { SelectMenuBookModalReturnData } from 'features/SaleType/components/SelectMenuBookModal';
import { TimeFrame } from 'features/Store/models/timeframe';
import timeFrameService from 'features/Store/timeFrameService';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, DragStart, DraggingStyle, DropResult, NotDraggingStyle } from 'react-beautiful-dnd';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { Control, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import EatingWaySettingRowList from '../EatingWaySettingRowList';

// constant
const ALL_YOU_CAN_EAT_PROPERTY = 2;

export interface EatingWaySettingContextProps {
  control?: Control<FormValues>;
  // timeFrameSource?: { [key: number]: TimeFrame };
  timeFrameSource?: TimeFrame[];
  columnWidth: number[];
  timeframeColumnWidth: number;
  totalRowWidth: number;
  getListStyle: (isDraggingOver: boolean) => object;
  getItemStyle: (
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
    isSelected?: boolean,
    isGhosting?: boolean
  ) => any;
  onSelectedRowChanged?: () => void;
}

const getListStyle = (isDraggingOver: boolean) => ({
  background: isDraggingOver ? '#999' : 'lightgrey',
});

const getItemStyle = (isDragging: boolean, draggableStyle: any, isSelected?: boolean, isGhosting?: boolean) => {
  let backgroundColor = '#fff';

  if (isGhosting) {
    backgroundColor = '#FAFBFC';
  }

  if (isSelected) {
    backgroundColor = '#DEEBFF';
  }

  if (isDragging) {
    backgroundColor = '#ffff8d';
  }

  return {
    userSelect: 'none',
    background: backgroundColor,
    opacity: isGhosting ? '0' : '1',
    ...draggableStyle,
  };
};

const COLUMN_WIDTH = [38, 154, 191];
const TIMEFRAME_COLUMN_WIDTH = 129;
export const EatingWaySettingContext = createContext<EatingWaySettingContextProps>({
  columnWidth: COLUMN_WIDTH,
  timeframeColumnWidth: TIMEFRAME_COLUMN_WIDTH,
  totalRowWidth: 0,
  getListStyle: getListStyle,
  getItemStyle: getItemStyle,
});

export interface EatingWaySettingModalProps {
  eatingWayId: number;
  saleTypeId: number;
  saleTypeName?: string;
  eatingWayName?: string;
  propertyName?: string;
  eatingWayProperty?: number;
  onClose?: (isDataChanged: boolean) => void;
}

export type FormValues = {
  eatingWaySettings: EatingWaySettingListItem[];
};

export function EatingWaySettingModal({
  eatingWayProperty,
  eatingWayId,
  saleTypeId,
  saleTypeName = '',
  eatingWayName = '',
  propertyName = '',
  onClose,
}: EatingWaySettingModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();

  // ref
  const btnDeleteRef = useRef<HTMLButtonElement>(null);
  const rootData = useRef<EatingWaySettingListItem[]>([]);
  const isFormSaved = useRef<boolean>(false);

  // selector
  const data = useAppSelector(selectEatingWaySettingData);
  const selectedItemIds = useAppSelector(selectEatingWaySettingSelectedItemIds);
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [showSelectMenuBookModal, setShowSelectMenuBookModal] = useState(false);
  const [exceptMenuBookIds, setExceptMenuBookIds] = useState<number[]>([]);
  const [exceptCourseIds, setExceptCourseIds] = useState<number[]>([]);
  const [timeFrameList, setTimeFrameList] = useState<TimeFrame[]>([]);
  // const [timeFrameMap, setTimeFrameMap] = useState<{ [key: number]: TimeFrame }>();

  // canculate values
  const totalRowWidth = useMemo(() => {
    const totalFixedColumnWidth = COLUMN_WIDTH.reduce((sum, item) => sum + item, 0);
    const totalTimeFrameColumnWidth = timeFrameList.length * TIMEFRAME_COLUMN_WIDTH;
    return totalFixedColumnWidth + totalTimeFrameColumnWidth;
  }, [timeFrameList]);

  // form
  const { control, handleSubmit, setValue, getValues } = useForm<FormValues>({
    defaultValues: {
      eatingWaySettings: [],
    },
  });
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'eatingWaySettings',
    keyName: 'key',
  });

  // status action buttons
  useEffect(() => {
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }, []);

  // load data
  function loadTimeFrameData() {
    (async () => {
      try {
        const timeFrameResponse = await timeFrameService.getAll({ sale_type_id: saleTypeId });
        setTimeFrameList(timeFrameResponse.data);
        // const timeFrameMap = timeFrameResponse.data.reduce((timeFrameMap: { [key: string]: TimeFrame }, timeframe) => {
        //   timeFrameMap[timeframe.id] = timeframe;
        //   return timeFrameMap;
        // }, {});
        // setTimeFrameMap(timeFrameMap);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function loadEatingWaySettingData() {
    (async () => {
      try {
        const response = await eatingWaySettingService.getAll(eatingWayId);
        rootData.current = response.data;
        dispatch(
          eatingWaySettingActions.updateState({
            data: response.data,
            selectedDroppableId: undefined,
            draggingItemId: undefined,
          })
        );
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  useEffect(() => {
    loadTimeFrameData();
    loadEatingWaySettingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue('eatingWaySettings', data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    // compose except_menu_book_ids, and except_course_ids
    const newExceptMenuBookIds: number[] = [];
    let newExceptCourseIds: number[] = [];
    fields.forEach(function (item) {
      if (item.course_id) {
        if (!newExceptCourseIds.includes(item.course_id)) {
          newExceptCourseIds.push(item.course_id);
        }
      } else {
        if (item.menu_book_id && !newExceptMenuBookIds.includes(item.menu_book_id)) {
          newExceptMenuBookIds.push(item.menu_book_id);
        }
      }
    });
    setExceptMenuBookIds(newExceptMenuBookIds);
    setExceptCourseIds(newExceptCourseIds);
  }, [fields]);

  function closeModal(isDataChanged: boolean = false) {
    onClose?.(isDataChanged);
    dispatch(
      eatingWaySettingActions.updateState({
        data: [],
        selectedDroppableId: undefined,
        draggingItemId: undefined,
      })
    );
  }

  function handleClose() {
    const isFormDataChanged = checkFormDataChanged();
    if (isFormDataChanged) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.info')}
          message={t('EatingWaySettingModal.warning_save_data')}
          onCancel={() => onClose?.(false)}
          onConfirm={() => {
            const payload = getValues('eatingWaySettings');

            if (!isCourseForBuffetPropertyValid(eatingWayProperty, payload)) {
              setTimeout(() => {
                showGlobalModal(() => (
                  <AlertModal title={t('App.warning')} message={t('EatingWaySettingModal.warning_invalid_course')} />
                ));
              }, 0);
              return;
            }

            (async () => {
              try {
                const response = await eatingWaySettingService.update(eatingWayId, saleTypeId, payload);
                showGlobalModal(() => (
                  <AlertModal title={t('App.info')} message={response.message} onClose={() => closeModal(true)} />
                ));
                rootData.current = payload;
              } catch (e) {
                if (!handleServiceError(showGlobalModal, e as AxiosError)) {
                  displayError(showGlobalModal, e as AxiosError);
                }
              }
            })();
          }}
        />
      ));
    } else {
      closeModal(isFormSaved.current);
    }
  }

  // SelectMenuBookModal events
  function handleOpenSelectMenuBookModal() {
    setShowSelectMenuBookModal(true);
  }

  function handleCloseSelectMenuBookModal() {
    setShowSelectMenuBookModal(false);
  }

  function handleReflectSelectMenuBookModal(returnData: SelectMenuBookModalReturnData[]) {
    const addEatingWaySettings: EatingWaySettingListItem[] = [];
    returnData.forEach((item) => {
      const addEatingWaySetting: EatingWaySettingListItem = {
        eating_way_id: eatingWayId,
        menu_book_id: item.menuBookId,
        menu_book_name: item.menuBookName,
        course_id: item.courseId,
        course_name: item.courseName,
        time_frames: [],
      };
      addEatingWaySettings.push(addEatingWaySetting);
    });

    if (addEatingWaySettings.length > 0) {
      append(addEatingWaySettings);
    }
  }

  function isArrayEatingWaySettingListItemEqual(
    arr1: EatingWaySettingListItem[],
    arr2: EatingWaySettingListItem[]
  ): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }

    return arr1.every((item1, index) => {
      const item2 = arr2[index];

      return (
        item1.eating_way_id === item2.eating_way_id &&
        item1.menu_book_id === item2.menu_book_id &&
        item1.menu_book_name === item2.menu_book_name &&
        item1.course_id === item2.course_id &&
        item1.course_name === item2.course_name &&
        isTimeFramesEqual(item1.time_frames, item2.time_frames)
      );
    });
  }

  function isTimeFramesEqual(tf1: EatingWaySettingTimeFrame[], tf2: EatingWaySettingTimeFrame[]): boolean {
    if (tf1.length !== tf2.length) {
      return false;
    }

    const sortedTf1 = [...tf1].sort((a, b) => a.time_frame_id - b.time_frame_id);
    const sortedTf2 = [...tf2].sort((a, b) => a.time_frame_id - b.time_frame_id);

    return sortedTf1.every((tf, index) => tf.time_frame_id === sortedTf2[index].time_frame_id);
  }

  function checkFormDataChanged() {
    const isSameData = isArrayEatingWaySettingListItemEqual(rootData.current, getValues('eatingWaySettings'));
    return !isSameData;
  }

  function isCourseForBuffetPropertyValid(property: number | undefined, eatingWaySettings: EatingWaySettingListItem[]) {
    // check only one menunook/course if property is all-you-can-eat/drink
    const menuBookCourseIds = eatingWaySettings.filter((item) => item.course_id).map((item) => item.menu_book_id);
    const uniqMenuBookCourseIds = Array.from(new Set(menuBookCourseIds));
    const courseIds = eatingWaySettings.filter((item) => item.course_id).map((item) => item.course_id);
    const uniqCourseIds = Array.from(new Set(courseIds));
    if (property === ALL_YOU_CAN_EAT_PROPERTY) {
      if (uniqCourseIds.length === 0 || uniqMenuBookCourseIds.length > 1) {
        return false;
      }
    }
    return true;
  }

  function onSubmit(formValues: FormValues) {
    if (!isCourseForBuffetPropertyValid(eatingWayProperty, formValues.eatingWaySettings)) {
      showGlobalModal(() => (
        <AlertModal title={t('App.warning')} message={t('EatingWaySettingModal.warning_invalid_course')} />
      ));
      return;
    }

    (async () => {
      try {
        const response = await eatingWaySettingService.update(eatingWayId, saleTypeId, formValues.eatingWaySettings);
        rootData.current = formValues.eatingWaySettings;
        isFormSaved.current = true;

        // update redux
        dispatch(
          eatingWaySettingActions.updateState({
            data: formValues.eatingWaySettings,
            selectedDroppableId: undefined,
            draggingItemId: undefined,
          })
        );

        showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} onClose={handleClose} />);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleDelete() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-ews-selection input:checked');
    if (chkSelectionElems) {
      const selectedRowIndexs: number[] = [];
      chkSelectionElems.forEach((element) => {
        selectedRowIndexs.push(Number(element.value));
      });

      if (selectedRowIndexs.length > 0) {
        remove(selectedRowIndexs);
      }
    }
  }

  function onDragStart(start: DragStart) {
    // const id: number = Number(start.draggableId.split('-').splice(-1).pop());
    const id: string = start.draggableId;
    const selected = selectedItemIds.find((itemId: string): boolean => itemId === id);

    // if dragging an item that is not selected - unselect all items
    if (!selected) {
      dispatch(eatingWaySettingActions.unselectAll());
    }
    dispatch(eatingWaySettingActions.setDraggingItemId(id));
  }

  function onDragEnd(result: DropResult) {
    const destination = result.destination;
    const source = result.source;

    // nothing to do
    if (!destination || result.reason === 'CANCEL') {
      dispatch(eatingWaySettingActions.setDraggingItemId(undefined));
      return;
    }

    // const type = result.type.split('-')[0];
    const droppableId = result.source.droppableId;
    const reorderData = mutliDragAwareReorder({
      data: getValues('eatingWaySettings'),
      selectedItemIds,
      source,
      destination,
      droppableId,
    });

    dispatch(
      eatingWaySettingActions.updateState({
        ...reorderData,
        selectedDroppableId: undefined,
        draggingItemId: undefined,
      })
    );
  }

  function handleSelectedRowChanged() {
    if (btnDeleteRef.current) {
      const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-ews-selection input:checked');

      if (chkSelectionElems.length === 0) {
        btnDeleteRef.current.disabled = true;
      } else {
        btnDeleteRef.current.disabled = false;
      }
    }
  }

  return (
    <>
      <Modal size="lg" dialogClassName="eating-way-setting-modal" show backdrop="static" onHide={handleClose} centered>
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={t('EatingWaySettingModal.head_title')}></FeatureTitleBar>
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <Row className="mb-3">
            <Col sm={6} className="fw-bold">
              <Row>
                <Col sm={3} className="text-end">
                  {t('EatingWaySettingModal.eating_way')}:
                </Col>
                <Col sm={9}>{eatingWayName}</Col>
              </Row>
              <Row>
                <Col sm={3} className="text-end">
                  {t('EatingWaySettingModal.property')}:
                </Col>
                <Col sm={9}>{propertyName}</Col>
              </Row>
            </Col>
            <Col sm={6} className="text-end">
              <Button type="submit" className="btn--dark-green" onClick={handleOpenSelectMenuBookModal}>
                {t('EatingWaySettingModal.add_new')}
              </Button>
            </Col>
          </Row>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Row className="mb-3">
              <Col sm={9} className="small">
                <div>{t('EatingWaySettingModal.guide_text1')}</div>
                <div className="ps-3">{t('EatingWaySettingModal.guide_text2')}</div>
                <div className="ps-3">{t('EatingWaySettingModal.guide_text3')}</div>
              </Col>
              <Col sm={3} className="text-end">
                <div className="text-end mb-2">
                  <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
                    {t('EatingWaySettingModal.delete')}
                  </Button>
                  <Button className="ms-3 btn--dark-green" type="submit">
                    {t('EatingWaySettingModal.save')}
                  </Button>
                </div>
              </Col>
            </Row>

            <EatingWaySettingContext.Provider
              value={{
                control: control,
                timeFrameSource: timeFrameList,
                columnWidth: COLUMN_WIDTH,
                timeframeColumnWidth: TIMEFRAME_COLUMN_WIDTH,
                totalRowWidth: totalRowWidth,
                getListStyle: getListStyle,
                getItemStyle: getItemStyle,
                onSelectedRowChanged: handleSelectedRowChanged,
              }}
            >
              <div className="dan-sticky-table" style={{ maxHeight: '440px' }}>
                <div className="dan-table">
                  <div className="dan-thead">
                    <div className="dan-tr">
                      <div
                        className="dan-th flex-grow-0 flex-shrink-0"
                        style={{ width: COLUMN_WIDTH[0], position: 'sticky', left: 0, top: 0 }}
                      >
                        {t('EatingWaySettingModal.selection')}
                      </div>
                      <div
                        className="dan-th flex-grow-0 flex-shrink-0"
                        style={{ width: COLUMN_WIDTH[1], position: 'sticky', left: COLUMN_WIDTH[0], top: 0 }}
                      >
                        {t('EatingWaySettingModal.selected_menu_book')}
                      </div>
                      <div
                        className="dan-th flex-grow-0 flex-shrink-0"
                        style={{
                          width: COLUMN_WIDTH[2],
                          position: 'sticky',
                          left: COLUMN_WIDTH[0] + COLUMN_WIDTH[1],
                          top: 0,
                          borderRight: '1px solid var(--bs-border-color)',
                        }}
                      >
                        {t('EatingWaySettingModal.selected_course')}
                      </div>
                      <div className="dan-th d-flex flex-column" style={{ padding: 0 }}>
                        <div className="text-center" style={{ padding: '0.5rem' }}>
                          <div>{t('EatingWaySettingModal.display_timeframe')}</div>
                          <div>
                            ({t('EatingWaySettingModal.sale_type')}：{saleTypeName})
                          </div>
                        </div>
                        <div className="dan-tr nested small">
                          {timeFrameList.map(function (timeFrame, index) {
                            return (
                              <div
                                key={timeFrame.id}
                                className="dan-th text-break"
                                style={{ width: TIMEFRAME_COLUMN_WIDTH }}
                              >
                                {timeFrame.name}
                              </div>
                            );
                          })}
                        </div>
                        <div className="dan-tr nested small">
                          {timeFrameList.map(function (timeFrame, index) {
                            return (
                              <div key={timeFrame.id} className="dan-th" style={{ width: TIMEFRAME_COLUMN_WIDTH }}>
                                {timeFrame.start}～{timeFrame.end}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    <EatingWaySettingRowList data={fields} />
                  </DragDropContext>
                </div>
              </div>
            </EatingWaySettingContext.Provider>
          </form>
        </Modal.Body>
      </Modal>

      {showSelectMenuBookModal &&
        createPortal(
          <SelectMenuBookModal
            exceptMenuBookIds={exceptMenuBookIds}
            exceptCourseIds={exceptCourseIds}
            onClose={handleCloseSelectMenuBookModal}
            onReflect={handleReflectSelectMenuBookModal}
          />,
          document.body
        )}
    </>
  );
}
